import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Pricing = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [showMonthly, setShowMonthly] = React.useState(false);

  return (
    <section id="pricing" className="section bg-white">
      <div className="container mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Introductory <span className="text-orange-dark-si">Limited Time</span> Offer
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Take advantage of our special pricing to celebrate the launch of Sales Intercept.
            These prices won't last long!
          </p>
        </div>

        {/* Promotional Banner */}
        <div className="bg-gradient-to-r from-purple-si to-orange-dark-si text-white p-4 rounded-lg text-center mb-12 max-w-3xl mx-auto">
          <p className="font-semibold mb-2">
            🔥 Limited Time Pricing — Lock in these rates before they increase! 🔥
          </p>
          <p className="text-sm">
            Kickstart your trial — on us!<br />
            We'll set everything up on your site and even load your first 3 videos. <span className="font-bold">$250 value, totally free.</span>
          </p>
          <p className="text-sm mt-2 font-bold">
            No Credit Card Required to Start Your Free Trial!
          </p>
        </div>

        {/* Pricing Toggle */}
        <div className="flex justify-center items-center gap-4 mb-8">
          <span className={`font-semibold ${!showMonthly ? 'text-purple-si' : 'text-gray-500'}`}>Annual</span>
          <button 
            onClick={() => setShowMonthly(!showMonthly)}
            className={`relative w-14 h-7 rounded-full transition-colors duration-300 focus:outline-none ${showMonthly ? 'bg-purple-si' : 'bg-gray-300'}`}
          >
            <div className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-transform duration-300 ${showMonthly ? 'translate-x-8' : 'translate-x-1'}`}></div>
          </button>
          <span className={`font-semibold ${showMonthly ? 'text-purple-si' : 'text-gray-500'}`}>Monthly</span>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-purple-si to-purple-light-si rounded-2xl overflow-hidden shadow-xl">
            <div className="p-8 text-white">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-3xl font-bold">Everything You Need</h2>
                  <p className="opacity-90">One simple plan, all features included</p>
                </div>
                <div className="bg-yellow-si/20 text-white font-bold py-1 px-3 rounded-full text-sm">
                  Limited Time
                </div>
              </div>

              <div className="flex items-center mb-8">
                {showMonthly ? (
                  <>
                    <div className="text-4xl font-bold">$67</div>
                    <div className="ml-2 opacity-90">/month</div>
                  </>
                ) : (
                  <>
                    <div className="text-4xl font-bold">$490</div>
                    <div className="ml-2 opacity-90">/year</div>
                    <div className="ml-2 text-sm opacity-90">(4 and a half months free!)</div>
                  </>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>3 Video Uploads ($250 setup included)</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Unlimited Views</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Advanced Targeting Options</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Performance Analytics</span>
                </div>
              </div>

              <div className="mt-8">
                <a 
                  href="https://dashboard.salesintercept.com/signup"
                  className="block w-full bg-white text-purple-si text-center py-3 rounded-lg font-semibold hover:bg-white/90 transition-colors"
                >
                  Start 14-Day Free Trial
                </a>
                <p className="text-center mt-2 text-sm font-medium">No Credit Card Required!</p>
              </div>
            </div>
          </div>
        </div>

        {/* Guarantees */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="text-center">
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-si/10 text-purple-si">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-bold mb-2">Risk Free Trial</h3>
            <p className="text-gray-600">Not satisfied? Get a full refund within your first 30 days</p>
          </div>
          
          <div className="text-center">
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-si/10 text-purple-si">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-bold mb-2">First Class Support</h3>
            <p className="text-gray-600">Our expert team is always ready to help you succeed</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;